import {
  Clock,
  Info,
  Pause,
  Play,
  RefreshCw,
  UsersRound,
} from 'lucide-react';
import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { cn, formatNumber } from '@/lib/utils';

import { ConsolidadoTurnosCard } from '../components/consolidado-turnos';
import { TurnoCard } from '../components/turno-card';
import { AUTO_REFRESH_MS, useTurnosEnVivo } from '../hooks/use-turnos-en-vivo';

/**
 * Turnos en vivo.
 *
 * Monitoreo en tiempo real de los turnos abiertos: quién está en turno, hace
 * cuánto, qué lados tiene asignados, cuánto lleva vendido y cómo está cobrando.
 * Es una vista de sólo lectura: el cierre y la liquidación siguen en el panel
 * anterior, por lo que aquí no se muestra el "por entregar".
 *
 * Se reconsulta cada 30 s mientras la actualización automática esté activa.
 */
export function TurnosPage() {
  useDocumentTitle('Turnos en vivo');

  const [autoRefresh, setAutoRefresh] = useState(true);
  const { data, isPending, isError, isFetching, refetch, isRefetching, dataUpdatedAt } =
    useTurnosEnVivo({ autoRefresh });

  const turnos = data?.turnos ?? [];
  const consolidado = data?.consolidado ?? null;

  const actualizado = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString('es-PE', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      })
    : null;

  return (
    <div className="space-y-6">
      {/* Encabezado */}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">
            Operación en tiempo real
          </p>
          <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
            <Clock className="size-6 text-brand-500" aria-hidden="true" />
            Turnos en vivo
          </h1>
          <p className="mt-1 text-sm text-ink-500">
            Turnos abiertos, ventas acumuladas y forma de cobro de cada operador.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {actualizado ? (
            <span className="text-xs text-ink-400">
              Actualizado {actualizado}
              {autoRefresh ? ` · cada ${AUTO_REFRESH_MS / 1000} s` : ''}
            </span>
          ) : null}

          <button
            type="button"
            aria-pressed={autoRefresh}
            onClick={() => setAutoRefresh((prev) => !prev)}
            title="Activar o desactivar la actualización automática"
            className={cn(
              'inline-flex items-center gap-2 rounded-control border px-3 py-2 text-xs font-medium transition-colors',
              autoRefresh
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                : 'border-ink-200 bg-white text-ink-500 hover:text-ink-800',
            )}
          >
            {autoRefresh ? (
              <Pause className="size-3.5" aria-hidden="true" />
            ) : (
              <Play className="size-3.5" aria-hidden="true" />
            )}
            {autoRefresh ? 'Automático' : 'Manual'}
          </button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => void refetch()}
            loading={isRefetching}
            disabled={isPending}
          >
            <RefreshCw
              className={cn('size-4', isFetching && !isRefetching && 'animate-spin')}
              aria-hidden="true"
            />
            Actualizar
          </Button>
        </div>
      </header>

      {isError ? (
        <Alert variant="error" title="No se pudieron cargar los turnos en vivo">
          <div className="flex flex-wrap items-center gap-3">
            <span>Verifica la conexión con el servidor e inténtalo nuevamente.</span>
            <Button size="sm" variant="outline" onClick={() => void refetch()} loading={isRefetching}>
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      {/* Consolidado */}
      {isPending ? (
        <div className="grid gap-px overflow-hidden rounded-card border border-ink-100 bg-ink-100 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-20 rounded-none" />
          ))}
        </div>
      ) : consolidado ? (
        <ConsolidadoTurnosCard consolidado={consolidado} className="animate-fade-in" />
      ) : null}

      {/* Turnos olvidados */}
      {consolidado && consolidado.vencidos > 0 ? (
        <Alert variant="warning" title="Turnos abiertos hace más de 24 horas">
          Hay {formatNumber(consolidado.vencidos)}{' '}
          {consolidado.vencidos === 1 ? 'turno' : 'turnos'} que siguen abiertos desde hace más de un
          día. Revisa si corresponden a cierres pendientes: mientras sigan abiertos, sus ventas se
          acumulan en el consolidado.
        </Alert>
      ) : null}

      {/* Listado */}
      {isPending ? (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-72 rounded-card" />
          ))}
        </div>
      ) : !isError && turnos.length === 0 ? (
        <div className="rounded-card border border-dashed border-ink-200 bg-white/70 px-6 py-16 text-center">
          <span className="mx-auto flex size-12 items-center justify-center rounded-2xl bg-ink-100 text-ink-400">
            <UsersRound className="size-6" aria-hidden="true" />
          </span>
          <h2 className="mt-4 text-base font-semibold text-ink-800">No hay turnos abiertos</h2>
          <p className="mx-auto mt-1.5 max-w-md text-sm leading-relaxed text-ink-500">
            En este momento ningún operador tiene un turno en curso. Los turnos aparecerán aquí en
            cuanto se abran.
          </p>
          <Button size="sm" variant="outline" className="mt-4" onClick={() => void refetch()}>
            <RefreshCw className="size-4" aria-hidden="true" />
            Actualizar
          </Button>
        </div>
      ) : (
        <section aria-label="Turnos abiertos" className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
          {turnos.map((turno) => (
            <TurnoCard key={turno.id} turno={turno} />
          ))}
        </section>
      )}

      {/* Nota */}
      {!isPending && !isError && turnos.length > 0 ? (
        <p className="flex items-start gap-2 text-xs leading-relaxed text-ink-400">
          <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
          El "cobrado en efectivo" se calcula sólo con las ventas registradas del turno: no incluye
          bóvedas, gastos, créditos ni pagos adelantados. El cuadre y el cierre del turno se
          realizan en el módulo de liquidaciones.
        </p>
      ) : null}
    </div>
  );
}
